import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { FC, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useGetUsersDetailByRoleMutation } from "../../../../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
} from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import {
  CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE,
  MODULE_PERMISSIONS,
  ROLE_IDS,
} from "../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import {
  formatFullNameWithPrefix,
  getFullName,
  getValueOrBlank,
  getValueOrBlankArray,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import useAccordion from "../../../../../../hooks/useAccordion";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { SalesCreditDetails } from "../../../../../createProgram/components/createProgramTabs/tabs/program/components/salesCredit/SalesCreditDetails";
import { isDeliveryCountryJapanNetherlands } from "../../../../../createProgram/helper";
import {
  ProgramDetail,
  ProgramStatusList,
  ProgramTabFormKey,
} from "../../../../../createProgram/modelsAndSchemas/Models";
import {
  programSiteCoreKeys,
  salesCreditSiteCoreKeys,
} from "../../../../../createProgram/siteCore/SiteCoreKeys";
import { ProgramStatusResult } from "./components/programStatus/ProgramStatusResult";
import { TabsWrapper } from "./tabs.style";

interface ProgramTabProp {
  pageId: string;
  programDetail?: ProgramDetail;
  programStatusList?: ProgramStatusList[];
  refetchProgramStatusList: () => void;
  isCMCProgram?: boolean;
  isTMAssessmentProgram?: boolean;
  isTMLeaderCoachingProgram?: boolean;
  isFromCoachPortal?: boolean;
}
const initialAccordionStatus = {
  all: true,
  programDetail: true,
  otherDetails: true,
  programStatus: true,
  salesCredit: true,
};

export const ProgramTab: FC<ProgramTabProp> = ({
  pageId,
  programDetail,
  programStatusList = [],
  refetchProgramStatusList,
  isCMCProgram = false,
  isTMAssessmentProgram = false,
  isTMLeaderCoachingProgram = false,
  isFromCoachPortal = false,
}) => {
  const {
    fetchComponentTitle,
    commonLabels,
    fetchLabel,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { isMobile } = useMediaQueries();

  const {
    mutateAsync: getClientRelationshipOwners,
    data: clientRelationshipOwnerList,
  } = useGetUsersDetailByRoleMutation();

  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);

  useEffect(() => {
    getClientRelationshipOwners({ roleIds: [ROLE_IDS.sales] });
  }, []);

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };
  const getRemoveIcon = () => {
    return <RemoveIcon color={"primary"} />;
  };

  const getAddIcon = () => {
    return <AddIcon color={"primary"} />;
  };

  const {
    officeDetailsKey,
    billingOffice,
    salesOfficeDetail,
    relationshipOwner,
    deliveryOffice,
    billingContact,
    hrContact,
    projectManager,
    projectCoordinator,
    programName,
    nextView,
    nextProgramClass,
    businessUnit,
    costCenter,
    pONumber,
    programDetails: programDetailsKey,
    participantInformation,
    participantName,
    prefixList,
    additionalInvoiceContact,
    participantsManager,
    businessApprovalNumber,
    clientInformation1,
    clientInformation2,
    clientInformation3,
    site,
    additionalHrContact,
    deliveryLead,
    hiringManager,
    dossierNumber,
    actualClient,
  } = programSiteCoreKeys;

  const getLabel = (parent: string, key: string) => {
    return fetchLabel(parent, key);
  };

  const { setValue } = useFormContext();

  useEffect(() => {
    if (isNotEmpty(programDetail)) {
      setValue(
        ProgramTabFormKey.salesCreditDetails,
        getValueOrBlankArray(programDetail?.salesCreditDetails),
      );
      setValue(
        ProgramTabFormKey.businessApprovalNumber,
        getValueOrBlank(programDetail?.businessApprovalNumber),
      );
    }
  }, [programDetail]);

  const showBusinessApprovalField = useMemo(() => {
    return isDeliveryCountryJapanNetherlands(programDetail?.deliveryCountry);
  }, [programDetail?.deliveryCountry]);

  const renderCommonTMFields = () => (
    <>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(officeDetailsKey, deliveryLead)}
          replaceEmptyWithDash
          value={getFullName(
            getValueOrBlank(
              programDetail?.deliveryLeadEngagementManager?.firstName,
            ),
            getValueOrBlank(
              programDetail?.deliveryLeadEngagementManager?.lastName,
            ),
            true,
          )}
          isValueWithEllipsis
        />
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(officeDetailsKey, additionalInvoiceContact)}
          replaceEmptyWithDash
          value={getFullName(
            getValueOrBlank(programDetail?.additionalInvoiceContact?.firstName),
            getValueOrBlank(programDetail?.additionalInvoiceContact?.lastName),
            true,
          )}
          isValueWithEllipsis
        />
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          label={getLabel(officeDetailsKey, participantsManager)}
          replaceEmptyWithDash
          value={getFullName(
            getValueOrBlank(programDetail?.participantManager?.firstName),
            getValueOrBlank(programDetail?.participantManager?.lastName),
            true,
          )}
          isValueWithEllipsis
        />
      </MuiGrid>
    </>
  );

  const isClientTypeNCBP = () => {
    return (
      programDetail?.clientType ===
      CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE
    );
  };

  return (
    <>
      <MuiBox className="header-collapse" id={`${pageId}-view-program`}>
        <MuiButtonTertiary
          id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
          onClick={handleExpandCollapseAllChange}
        >
          {accordionStatus.all
            ? commonLabels.collapseAll
            : commonLabels.expandAll}
          {accordionStatus.all ? getRemoveIcon() : getAddIcon()}
        </MuiButtonTertiary>
      </MuiBox>

      <TabsWrapper>
        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_PROGRAM_DETAILS
              : MODULE_PERMISSIONS.VIEW_PROGRAM_DETAILS
          }
        >
          <AccordionHeader
            wrapperClassName="program-accordion"
            title={renderHeader(programSiteCoreKeys.programDetails)}
            onChange={() => handleIndividualAccordionChange("programDetail")}
            isExpanded={accordionStatus.programDetail}
          >
            <MuiBox className="content">
              <MuiGrid container spacing={isMobile ? 0 : 2}>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(participantInformation, participantName)}
                    replaceEmptyWithDash
                    isValueWithEllipsis
                    value={formatFullNameWithPrefix(
                      getValueOrBlank(programDetail?.lastName),
                      getValueOrBlank(programDetail?.firstName),
                      fetchValueByKeyOfDropdown(
                        participantInformation,
                        prefixList,
                        getValueOrBlank(programDetail?.prefixKey),
                      ),
                    )}
                  />
                </MuiGrid>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, programName)}
                    replaceEmptyWithDash
                    value={programDetail?.programName}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, nextView)}
                    replaceEmptyWithDash
                    value={programDetail?.solutionOffering}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, nextProgramClass)}
                    replaceEmptyWithDash
                    value={programDetail?.programClass}
                    isValueWithEllipsis
                  />
                </MuiGrid>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, pONumber)}
                    replaceEmptyWithDash
                    value={programDetail?.poNumber?.name}
                    isValueWithEllipsis
                  />
                </MuiGrid>
                <MuiGrid size={gridSize} className={gridSizeMediumClass} />

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, businessUnit)}
                    replaceEmptyWithDash
                    value={programDetail?.businessUnit}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, costCenter)}
                    replaceEmptyWithDash
                    value={programDetail?.costCenter}
                    isValueWithEllipsis
                  />
                </MuiGrid>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, site)}
                    replaceEmptyWithDash
                    value={programDetail?.site}
                    isValueWithEllipsis
                  />
                </MuiGrid>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, clientInformation1)}
                    replaceEmptyWithDash
                    value={programDetail?.clientInformation1}
                    isValueWithEllipsis
                  />
                </MuiGrid>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, clientInformation2)}
                    replaceEmptyWithDash
                    value={programDetail?.clientInformation2}
                    isValueWithEllipsis
                  />
                </MuiGrid>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, clientInformation3)}
                    replaceEmptyWithDash
                    value={programDetail?.clientInformation3}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(programDetailsKey, dossierNumber)}
                    replaceEmptyWithDash
                    value={programDetail?.dossierNumber}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                {isClientTypeNCBP() && (
                  <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                    <MuiDoubleLabel
                      label={getLabel(programDetailsKey, actualClient)}
                      replaceEmptyWithDash
                      value={programDetail?.actualClientName}
                      isValueWithEllipsis
                    />
                    <MuiTypography
                      component="h4"
                      className="value-style"
                      variant="h4Bold"
                    >
                      {programDetail?.actualClientCode}
                    </MuiTypography>
                  </MuiGrid>
                )}
              </MuiGrid>
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>

        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_OTHER_DETAILS
              : MODULE_PERMISSIONS.VIEW_OTHER_DETAILS
          }
        >
          <AccordionHeader
            wrapperClassName="program-accordion"
            title={renderHeader(programSiteCoreKeys.officeDetailsKey)}
            onChange={() => handleIndividualAccordionChange("otherDetails")}
            isExpanded={accordionStatus.otherDetails}
          >
            <MuiBox className="content">
              <MuiGrid container spacing={isMobile ? 0 : 2}>
                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, billingOffice)}
                    replaceEmptyWithDash
                    value={programDetail?.billingOffice}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, salesOfficeDetail)}
                    replaceEmptyWithDash
                    value={programDetail?.salesOffice}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, relationshipOwner)}
                    replaceEmptyWithDash
                    value={getFullName(
                      getValueOrBlank(
                        programDetail?.clientRelationshipOwnerFirstName,
                      ),
                      getValueOrBlank(
                        programDetail?.clientRelationshipOwnerLastName,
                      ),
                      true,
                    )}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, deliveryOffice)}
                    replaceEmptyWithDash
                    value={getValueOrBlank(programDetail?.deliveryOffice)}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, billingContact)}
                    replaceEmptyWithDash
                    value={getFullName(
                      getValueOrBlank(programDetail?.billingFirstName),
                      getValueOrBlank(programDetail?.billingLastName),
                      true,
                    )}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, hrContact)}
                    replaceEmptyWithDash
                    value={getFullName(
                      getValueOrBlank(programDetail?.hrContactFirstName),
                      getValueOrBlank(programDetail?.hrContactLastName),
                      true,
                    )}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                {isTMAssessmentProgram && (
                  <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                    <MuiDoubleLabel
                      label={getLabel(officeDetailsKey, additionalHrContact)}
                      replaceEmptyWithDash
                      value={getFullName(
                        getValueOrBlank(
                          programDetail?.additionalHRContact?.firstName,
                        ),
                        getValueOrBlank(
                          programDetail?.additionalHRContact?.lastName,
                        ),
                        true,
                      )}
                      isValueWithEllipsis
                    />
                  </MuiGrid>
                )}

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, projectManager)}
                    replaceEmptyWithDash
                    value={getFullName(
                      getValueOrBlank(programDetail?.projectManagerFirstName),
                      getValueOrBlank(programDetail?.projectManagerLastName),
                      true,
                    )}
                    isValueWithEllipsis
                  />
                </MuiGrid>

                <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                  <MuiDoubleLabel
                    label={getLabel(officeDetailsKey, projectCoordinator)}
                    replaceEmptyWithDash
                    value={getFullName(
                      getValueOrBlank(
                        programDetail?.projectCoordinatorFirstName,
                      ),
                      getValueOrBlank(
                        programDetail?.projectCoordinatorLastName,
                      ),
                      true,
                    )}
                    isValueWithEllipsis
                  />
                </MuiGrid>
                {showBusinessApprovalField && (
                  <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                    <MuiDoubleLabel
                      label={getLabel(officeDetailsKey, businessApprovalNumber)}
                      replaceEmptyWithDash
                      value={getValueOrBlank(
                        programDetail?.businessApprovalNumber,
                      )}
                      isValueWithEllipsis
                    />
                  </MuiGrid>
                )}

                {isCMCProgram && (
                  <>
                    <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                      <MuiDoubleLabel
                        label={getLabel(
                          officeDetailsKey,
                          additionalInvoiceContact,
                        )}
                        replaceEmptyWithDash
                        value={getFullName(
                          getValueOrBlank(
                            programDetail?.additionalInvoiceContact?.firstName,
                          ),
                          getValueOrBlank(
                            programDetail?.additionalInvoiceContact?.lastName,
                          ),
                          true,
                        )}
                        isValueWithEllipsis
                      />
                    </MuiGrid>

                    <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                      <MuiDoubleLabel
                        label={getLabel(officeDetailsKey, participantsManager)}
                        replaceEmptyWithDash
                        value={getFullName(
                          getValueOrBlank(
                            programDetail?.participantManager?.firstName,
                          ),
                          getValueOrBlank(
                            programDetail?.participantManager?.lastName,
                          ),
                          true,
                        )}
                        isValueWithEllipsis
                      />
                    </MuiGrid>
                  </>
                )}

                {isTMAssessmentProgram && (
                  <>
                    {renderCommonTMFields()}

                    <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                      <MuiDoubleLabel
                        label={getLabel(officeDetailsKey, hiringManager)}
                        replaceEmptyWithDash
                        value={getValueOrBlank(programDetail?.hiringManager)}
                        isValueWithEllipsis
                      />
                    </MuiGrid>
                  </>
                )}

                {isTMLeaderCoachingProgram && renderCommonTMFields()}
              </MuiGrid>
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>

        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_PROGRAM_STATUS
              : MODULE_PERMISSIONS.VIEW_PROGRAM_STATUS
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.programStatus)}
            onChange={() => handleIndividualAccordionChange("programStatus")}
            isExpanded={accordionStatus.programStatus}
          >
            <MuiBox className="program-status">
              <MuiDoubleLabel
                label={fetchLabel(
                  programSiteCoreKeys.programStatus,
                  programSiteCoreKeys.currentStatus,
                )}
                value={""}
                isValueWithEllipsis
              />
              <StatusBadge
                status={programDetail?.programStatus as StatusBadgeType}
                label={fetchValueByKeyOfDropdown(
                  programSiteCoreKeys.generalDetails,
                  programSiteCoreKeys.statusList,
                  programDetail?.programStatus as StatusBadgeType,
                )}
              />
            </MuiBox>
            <MuiBox className="program-status-list">
              <ProgramStatusResult
                pageId={pageId}
                rows={programStatusList}
                refetchProgramStatusList={refetchProgramStatusList}
                isViewProgram
                deliveryCountry={programDetail?.deliveryCountry}
              />
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>

        {!isFromCoachPortal && (
          <PermissionWrapper permission={MODULE_PERMISSIONS.VIEW_SALES_CREDIT}>
            <MuiBox>
              <AccordionHeader
                title={renderHeader(salesCreditSiteCoreKeys.salesCredit)}
                onChange={() => handleIndividualAccordionChange("salesCredit")}
                isExpanded={accordionStatus.salesCredit}
              >
                <SalesCreditDetails
                  clientRelationshipOwnerList={
                    clientRelationshipOwnerList ?? []
                  }
                  pageId={pageId}
                  isInViewMode={true}
                  resultsFormKey={ProgramTabFormKey.salesCreditDetails}
                />
              </AccordionHeader>
            </MuiBox>
          </PermissionWrapper>
        )}
      </TabsWrapper>
    </>
  );
};
